/**
 * Maps intake form submission data to wizard defaultValues (Sprint 12).
 *
 * Uses label-based heuristic matching to identify fields that correspond to
 * known wizard step inputs. The manufacturing template fields map cleanly to
 * step1 (project name, NAICS) and step2 (capex, permanent jobs, construction
 * timeline). Fields with unrecognized labels are silently skipped.
 *
 * This function is intentionally pure — no API calls, no side effects.
 */

import type { FormField } from './types';

export interface WizardDefaults {
  step1?: {
    projectName?: string;
    naicsCode?: string;
    projectDescription?: string;
  };
  step2?: {
    capex?: number;
    permanentJobs?: number;
    constructionMonths?: number;
  };
}

interface LabelMapping {
  step: keyof WizardDefaults;
  key: string;
}

const LABEL_MAP: Record<string, LabelMapping> = {
  'project name': { step: 'step1', key: 'projectName' },
  'project description': { step: 'step1', key: 'projectDescription' },
  'naics code': { step: 'step1', key: 'naicsCode' },
  'capital investment': { step: 'step2', key: 'capex' },
  'capital investment in $': { step: 'step2', key: 'capex' },
  'capital investment ($)': { step: 'step2', key: 'capex' },
  'permanent jobs': { step: 'step2', key: 'permanentJobs' },
  'permanent jobs created': { step: 'step2', key: 'permanentJobs' },
  'full-time jobs': { step: 'step2', key: 'permanentJobs' },
  'construction timeline': { step: 'step2', key: 'constructionMonths' },
  'construction timeline in months': { step: 'step2', key: 'constructionMonths' },
  'construction timeline (months)': { step: 'step2', key: 'constructionMonths' },
};

/** Build a wizard defaultValues object from form field definitions + submission data. */
export function mapSubmissionToWizard(
  fields: FormField[],
  data: Record<string, unknown>,
): WizardDefaults {
  const result: Record<string, Record<string, unknown>> = {};

  for (const field of fields) {
    const mapping = LABEL_MAP[field.label.toLowerCase().trim()];
    if (!mapping) continue;
    const value = data[field.id];
    if (value === undefined || value === null || value === '') continue;
    if (!result[mapping.step]) result[mapping.step] = {};
    // biome-ignore lint/style/noNonNullAssertion: result[mapping.step] is guaranteed non-null by the initializer two lines above
    result[mapping.step]![mapping.key] = value;
  }

  return result as WizardDefaults;
}

/** Returns a preview list of fields that will be mapped, for display before promoting. */
export function getMappedFieldsSummary(
  fields: FormField[],
  data: Record<string, unknown>,
): { label: string; value: unknown }[] {
  const out: { label: string; value: unknown }[] = [];
  for (const field of fields) {
    if (!LABEL_MAP[field.label.toLowerCase().trim()]) continue;
    const value = data[field.id];
    if (value === undefined || value === null || value === '') continue;
    out.push({ label: field.label, value });
  }
  return out;
}
